import { PlusIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import DeleteModal from "../../../../components/DeleteModal/DeleteModal";
import { apiV3 } from "../../../../utils/helpers/api";
import { useToast } from "../../../../components/Toastr/Toastr";

// Unified kiosk app: access-level options offered by this location's PreCheck
// kiosks. Each option maps to a standard zone (ordinary ASICs) and an exempt
// zone (Operations Staff / ASIC exemptions); the zone's audit configuration
// decides the screening tier server-side. Kiosks only ever see id + label.
export default function AccessLevelsSection({ locationId }: { locationId: string }) {
  const { showSuccessToast, showErrorToast } = useToast();
  const [loading, setLoading] = useState<boolean>(true);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [accessLevels, setAccessLevels] = useState<any[]>([]);
  const [zoneLocations, setZoneLocations] = useState<any[]>([]);
  const [newLabel, setNewLabel] = useState<string>("");
  const [newSortOrder, setNewSortOrder] = useState<string>("1");
  const [newStandardLocationId, setNewStandardLocationId] = useState<string>("");
  const [newExemptLocationId, setNewExemptLocationId] = useState<string>("");
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [accessLevelIdToDelete, setAccessLevelIdToDelete] = useState<string>("");

  useEffect(() => {
    fetchAccessLevels();
    fetchZoneLocations();
  }, []);

  const fetchAccessLevels = async () => {
    try {
      const res = await apiV3.getLocationAccessLevels(locationId);
      if (res.status === 200) {
        setAccessLevels(res.data);
      }
    } catch (error) {
      console.error("Error occurred while fetching access levels - ", error);
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  const fetchZoneLocations = async () => {
    try {
      // Admin list (no display filter): APAM zone locations are usually
      // hidden from kiosks but must be selectable here.
      const res = await apiV3.getLocations(1, 500, "", "", "asc", "name");
      if (res.status === 200) {
        setZoneLocations(res.data.data.filter((location: any) => location.id !== locationId));
      }
    } catch (error) {
      console.error("Error occurred while fetching locations - ", error);
      showErrorToast();
    }
  };

  const addAccessLevel = async () => {
    setIsBusy(true);
    try {
      await apiV3.createLocationAccessLevel(locationId, {
        label: newLabel.trim(),
        sortOrder: parseInt(newSortOrder, 10) || 0,
        standardLocationId: newStandardLocationId,
        exemptLocationId: newExemptLocationId,
      });
      showSuccessToast("Successfully added access level");
      setNewLabel("");
      setNewSortOrder(`${accessLevels.length + 2}`);
      setNewStandardLocationId("");
      setNewExemptLocationId("");
      await fetchAccessLevels();
    } catch (error: any) {
      console.error("Error occurred while adding access level - ", error);
      if (error?.response?.status === 409) {
        showErrorToast("An access level with that label already exists for this location");
      } else {
        showErrorToast();
      }
    } finally {
      setIsBusy(false);
    }
  };

  const toggleAccessLevel = async (accessLevel: any) => {
    setIsBusy(true);
    try {
      await apiV3.updateLocationAccessLevelById(locationId, accessLevel.id, {
        active: !accessLevel.active,
      });
      showSuccessToast(
        `Successfully ${accessLevel.active ? "deactivated" : "activated"} access level`
      );
      await fetchAccessLevels();
    } catch (error) {
      console.error("Error occurred while updating access level - ", error);
      showErrorToast();
    } finally {
      setIsBusy(false);
    }
  };

  const deleteAccessLevel = async () => {
    setIsBusy(true);
    setOpenDeleteModal(false);
    try {
      await apiV3.deleteLocationAccessLevelById(locationId, accessLevelIdToDelete);
      showSuccessToast("Successfully deleted access level");
      setAccessLevelIdToDelete("");
      await fetchAccessLevels();
    } catch (error) {
      console.error("Error occurred while deleting access level - ", error);
      showErrorToast();
    } finally {
      setIsBusy(false);
    }
  };

  const zoneName = (zone: any) => zone?.name ?? "—";

  return (
    <div className="mt-6">
      <h4 className="text-md leading-6 font-medium text-gray-900">Access Levels</h4>
      <p className="mt-1 text-sm text-gray-500">
        Options shown on this location's PreCheck kiosks. Each option routes the check-in to a
        zone location whose audit configuration decides the screening tier — the standard zone
        for ordinary ASIC holders, the exempt zone for Operations Staff. Leave empty for kiosks
        that don't ask for an access level. To change a mapping, delete the option and re-add it.
      </p>
      {loading ? (
        <div className="mt-2 text-sm text-gray-500">Loading...</div>
      ) : accessLevels.length === 0 ? (
        <div className="mt-2 text-sm text-gray-500">
          No access levels for this location — kiosks here skip the access level screen.
        </div>
      ) : (
        <div className="my-3">
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr className="text-left text-sm font-semibold text-gray-900">
                <th className="py-2 pr-3">Order</th>
                <th className="py-2 pr-3">Label</th>
                <th className="py-2 pr-3">Standard Zone</th>
                <th className="py-2 pr-3">Operations Staff Zone</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {accessLevels.map((accessLevel: any) => (
                <tr key={accessLevel.id} className="text-sm">
                  <td className="py-3 pr-3 text-gray-500">{accessLevel.sortOrder}</td>
                  <td className="py-3 pr-3 font-medium text-gray-900">{accessLevel.label}</td>
                  <td className="py-3 pr-3 text-gray-500">
                    {zoneName(accessLevel.standardLocation)}
                  </td>
                  <td className="py-3 pr-3 text-gray-500">
                    {zoneName(accessLevel.exemptLocation)}
                  </td>
                  <td className="py-3 pr-3">
                    {accessLevel.active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    <a
                      onClick={() => !isBusy && toggleAccessLevel(accessLevel)}
                      className="text-amber-600 hover:text-amber-800 cursor-pointer"
                    >
                      {accessLevel.active ? "Deactivate" : "Activate"}
                    </a>
                    <a
                      onClick={() => {
                        setOpenDeleteModal(true);
                        setAccessLevelIdToDelete(accessLevel.id);
                      }}
                      className="ml-4 text-red-600 hover:text-red-900 cursor-pointer"
                    >
                      Delete
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
        <div className="sm:col-span-2">
          <label htmlFor="access-level-label" className="block text-sm font-medium text-gray-700">
            Label
          </label>
          <input
            type="text"
            id="access-level-label"
            value={newLabel}
            disabled={isBusy}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="e.g. Arrival Only"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
          />
        </div>
        <div className="sm:col-span-1">
          <label htmlFor="access-level-order" className="block text-sm font-medium text-gray-700">
            Order
          </label>
          <input
            type="number"
            id="access-level-order"
            value={newSortOrder}
            disabled={isBusy}
            onChange={(e) => setNewSortOrder(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
          />
        </div>
        <div className="sm:col-span-3">
          <label
            htmlFor="access-level-standard"
            className="block text-sm font-medium text-gray-700"
          >
            Standard Zone
          </label>
          <select
            id="access-level-standard"
            value={newStandardLocationId}
            disabled={isBusy}
            onChange={(e) => setNewStandardLocationId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-amber-500 sm:text-sm"
          >
            <option value="">Select a zone location...</option>
            {zoneLocations.map((location: any) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-3">
          <label htmlFor="access-level-exempt" className="block text-sm font-medium text-gray-700">
            Operations Staff Zone
          </label>
          <select
            id="access-level-exempt"
            value={newExemptLocationId}
            disabled={isBusy}
            onChange={(e) => setNewExemptLocationId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-amber-500 sm:text-sm"
          >
            <option value="">Select a zone location...</option>
            {zoneLocations.map((location: any) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-3 flex items-end">
          <button
            type="button"
            onClick={() => addAccessLevel()}
            disabled={isBusy || !newLabel.trim() || !newStandardLocationId || !newExemptLocationId}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Access Level
          </button>
        </div>
      </div>
      <DeleteModal
        description="Deleting an access level removes the option from this location's kiosks within a minute. Existing check-ins are unaffected."
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        title={"Are you sure you want to delete this access level?"}
        action={() => deleteAccessLevel()}
        actionButtonText={"Delete"}
      />
    </div>
  );
}
